import * as React from "react"
import { Head, usePage } from "@inertiajs/react"
import type { Components } from "react-markdown"
import PublicLayout from "@/components/PublicLayout"
import { HeroBand, type HeroPhoto } from "@/components/gisela/HeroBand"
import { ContactBand, PhotoEnquiryBand } from "@/components/gisela/ClosingBands"
import GiselaPageTiles from "@/components/gisela/GiselaPageTiles"
import {
  RelatedArticlesBand,
  RelatedArticlesSidebar,
  type RelatedArticle,
} from "@/components/gisela/RelatedArticles"
import { TextBand, bandBg } from "@/components/gisela/TextBand"
import { useLocalePath } from "@/lib/locale-path"
import { cn } from "@/lib/utils"
import { presentBands } from "@/lib/section-order"
import { sectionHidden } from "@/lib/section-visibility"
import type { PageMeta, PageProps, PlacementPhoto } from "@/types/inertia"

interface Props {
  content: {
    hero: { heading?: string; subheading?: string; photo?: HeroPhoto | null; _hidden?: boolean }
    who_its_for: { body?: string }
    how_it_works: { body?: string }
    evidence: { body?: string }
    emdr: { body?: string }
    spanish: { body?: string }
    fees: { heading?: string; body?: string }
    faq: { body?: string }
    // The closing pair every page of hers ends on.
    cta?: { photo?: HeroPhoto | null; _hidden?: boolean }
    contact?: { heading?: string; body?: string; _hidden?: boolean }
  }
  articles?: RelatedArticle[]
  /** The home page's tile slot — one card per page of her site. */
  /** The desk-and-laptop picture beside "How online sessions work" — this
   *  page's own, or the home page's online-card photo until one is placed. */
  online_photo?: PlacementPhoto | null
  tile_photos?: Record<string, PlacementPhoto | null>
  /** A room of hers, standing beside the short enquiry form. */
  contact_photo?: PlacementPhoto | null
  /** Registry entry ids in display order — the admin's arrangement. */
  section_order?: string[]
  meta?: PageMeta
}

export default function OnlineTherapy({
  content,
  articles,
  online_photo,
  tile_photos = {},
  contact_photo,
  section_order,
  meta,
}: Props) {
  const { props } = usePage<PageProps>()
  const t = props.translations.gisela_pages.online
  const tc = props.translations.gisela_pages.common
  const lp = useLocalePath()
  // Search metadata falls back to the locale files, so the Spanish page gets a
  // Spanish title even before an admin saves a PageMetadata row.
  const title = meta?.title ?? t.meta_title
  const description = meta?.description ?? t.meta_description

  // Body links are authored as plain English paths in the CMS; lp() keeps a
  // Spanish reader on the Spanish side of the site.
  const markdown: Components = {
    a: ({ href, children }) => <a href={lp(href ?? "")}>{children}</a>,
  }

  // Each band names its registry entry (PageContentRegistry) so the admin's
  // order applies; empty ones drop out and the colours alternate over the rest.
  const bands = presentBands(
    [
      {
        id: "online-who-its-for",
        present: !!content.who_its_for.body,
        render: (i) => (
          <TextBand index={i} heading={t.who_heading} body={content.who_its_for.body} markdown={markdown} />
        ),
      },
      {
        id: "online-how-it-works",
        present: !!content.how_it_works.body,
        render: (i) => (
          <TextBand index={i} heading={t.how_heading} body={content.how_it_works.body} photo={online_photo} markdown={markdown} />
        ),
      },
      {
        id: "online-evidence",
        present: !!content.evidence.body,
        render: (i) => (
          <TextBand index={i} heading={t.evidence_heading} body={content.evidence.body} markdown={markdown} />
        ),
      },
      {
        id: "online-emdr",
        present: !!content.emdr.body,
        render: (i) => (
          <TextBand index={i} heading={t.emdr_heading} body={content.emdr.body} markdown={markdown} />
        ),
      },
      {
        id: "online-spanish",
        present: !!content.spanish.body,
        render: (i) => (
          <TextBand
            index={i}
            heading={t.spanish_heading}
            body={content.spanish.body}
            markdown={markdown}
            className="body-content mt-6 rounded-2xl border border-sage-light bg-white p-6 text-ink-body"
          >
            <p className="mt-4">
              <a href={lp("/therapy-in-spanish")}>{t.spanish_link}</a>
            </p>
          </TextBand>
        ),
      },
      {
        id: "online-fees",
        present: !!content.fees.body,
        render: (i) => (
          <TextBand
            index={i}
            heading={content.fees.heading || tc.fees}
            body={content.fees.body}
            markdown={markdown}
          />
        ),
      },
      {
        id: "online-faq",
        present: !!content.faq.body,
        render: (i) => (
          <TextBand index={i} heading={tc.common_questions} body={content.faq.body} markdown={markdown} />
        ),
      },
    ],
    section_order,
  )

  return (
    <>
      <Head title={title}>
        <meta name="description" content={description} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
      </Head>
      <PublicLayout>
        {/* Hero */}
        {!sectionHidden(content.hero) && (
          <HeroBand photo={content.hero.photo}>
            <p className="mb-3 text-xs font-medium uppercase tracking-widest text-white/60">
              {t.eyebrow}
            </p>
            <h1 className="text-white">{content.hero.heading || t.hero_heading}</h1>
            {content.hero.subheading && (
              <p className="mt-4 max-w-2xl text-lg text-white/80">{content.hero.subheading}</p>
            )}
          </HeroBand>
        )}

        {/* Everything below the hero reserves the right gutter (xl+) for the
            related-articles sidebar; the bands' full-bleed backgrounds still
            span the viewport because the padding sits on each section. */}
        {/* The gutter keeps a column's worth of height when the sidebar is
            in it: a page whose bands are shorter than its reading list would
            otherwise leave the sticky column nowhere to sit, and it would
            spill over the bands the page closes with. */}
        <div
          className={cn(
            "relative xl:[&>section]:pr-80",
            !!articles?.length && "xl:min-h-[34rem]",
          )}
        >
          {bands.map((band, i) => (
            <React.Fragment key={band.id}>{band.render(i)}</React.Fragment>
          ))}

          {/* Articles filed under this page (articles.page_slug) — absent
              until one is linked. A band below xl, a sidebar above. */}
          <RelatedArticlesBand articles={articles} className={bandBg(bands.length)} />

          <RelatedArticlesSidebar articles={articles} />
        </div>

        {/* The close every page of hers ends on — the invitation, the tiles
            out of it, then a room beside the short form. The ask is the free
            consultation now, rather than the enquiry form on the Contact
            page: that form is the band below. */}
        {!sectionHidden(content.cta) && (
          <ContactBand heading={t.cta_heading} body={t.cta_body} photo={content.cta?.photo} />
        )}

        {/* This page has no tile of its own to leave out. */}
        <GiselaPageTiles photos={tile_photos} />

        {!sectionHidden(content.contact) && (
          <PhotoEnquiryBand
            heading={content.contact?.heading}
            body={content.contact?.body}
            photo={contact_photo}
          />
        )}
      </PublicLayout>
    </>
  )
}
